import axios, { Axios, AxiosError } from 'axios';
import { API_URL } from '../config';
import { Fields } from "../types";
import { AxiosRequestConfig } from 'axios';

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  permission?: string;
}


// login call
export const login = async (data: any) => {

  let body = {
    codice_azienda: parseInt(data.codice),
    email: data.email,
    password: data.password,
    debug: false
  }

  if (import.meta.env.VITE_APP_ENV === 'development') {
    body = {
      ...body,
      // debug: true
    }
  }

  const response = await axios.post(`${API_URL}/login`, body, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  return response.data;
}

export const whoami = async (token: string) => {
  const response = await axios.get(`${API_URL}/me`, {
    headers: {
      'Authorization': `${token}`,
    }
  })
  return response.data;
}

export const singupFields = async (): Promise<Fields> => {
  try {
    const response = await axios.get<Fields>(`${API_URL}/signup?ajax=1`);

    return response.data;
  } catch (e) {
    const error = e as AxiosError;
    throw new Error(`Error in 'singupFields': ${error.message}`);
  }
};


type SignupResponseSuccess = {
  success: boolean;
}

export type SignupResponseError = {
  error: any;
}

export const singup = async (data: Record<string, any>): Promise<SignupResponseSuccess> => {
  const response = await axios.post(
    `${API_URL}/signup?ajax=1`,
    data,
  );

  return response.data;
};

export const singupShort = async (data: Record<string, any>): Promise<SignupResponseSuccess> => {
  console.log(data);
  
  const response = await axios.post(
    `${API_URL}/signup-short`,
    data,
  );

  return response.data;
};


export const fetchEmployees = async (token: string, q: string | null = null, signal: AbortSignal | null = null, permission: string | null) => {
  const config: any = {};

  if(signal !== null)
    config.signal = signal;

  if(q !== null)
    config.params = {q};

  if (permission) 
    config.permission = permission;
  
  const response = await axios.get(`${API_URL}/dipendenti`, {
    headers: {
      'Authorization': `${token}`,
    },
    ...config
  })
  return response.data;
}

export const fetchBuste = async (token: string, employee: any, permission: string | null) => {
  const response = await axios.get(`${API_URL}/buste_paga${employee != false ? ('?employee=' + employee) : ''}`, {
    headers: {
      'Authorization': `${token}`,
    },
    permission,
  } as CustomAxiosRequestConfig);
  return response.data;
}

export const getPayrollYears = async (
  token: string,
  signal: AbortSignal | null = null,
  permission: string | null,
  employee: any | null = null,
) => {
  const config: any = {};
  if (signal) config.signal = signal;
  if (permission) config.permission = permission;

  const params: any = {};
  if (employee) params.employee = employee;

  const response = await axios.get(`${API_URL}/v2/payrolls/years/`, {
    headers: { Authorization: `${token}` },
    params,
    ...config,
  });

  return response.data;
};


export const getPayrollMonthsByYear = async (
  token: string,
  year: number,
  signal: AbortSignal | null = null,
  permission: string | null,
  employee: any | null = null,
) => {
  const config: any = {};
  if (signal) config.signal = signal;
  if (permission) config.permission = permission;

  const params: any = {};
  if (employee) params.employee = employee;

  const response = await axios.get(`${API_URL}/v2/payrolls/years/${year}/months/`, {
    headers: { Authorization: `${token}` },
    params,
    ...config,
  });

  return response.data;
};

export const getPayrollListByMonth = async (
  token: string,
  year: number,
  month: number,
  signal: AbortSignal | null = null,
  permission: string | null,
  employee: any | null = null,
  extraParams: { page?: number; page_size?: number; search?: string } = {} // ✅
) => {
  const config: any = {};
  if (signal) config.signal = signal;
  if (permission) config.permission = permission;

  const params: any = { ...extraParams };
  if (employee) params.employee = employee;

  const response = await axios.get(`${API_URL}/v2/payrolls/years/${year}/months/${month}/`, {
    headers: { Authorization: `${token}` },
    params,
    ...config,
  });

  return response.data;
};

export const fetchCu = async (token: string, employee: any, permission: string | null) => {
  const response = await axios.get(`${API_URL}/cu${employee != false ? ('?employee=' + employee) : ''}`, {
    headers: {
      'Authorization': `${token}`,
    },
    permission,
  } as CustomAxiosRequestConfig);
  return response.data;
}

export const getCuYears = async (
  token: string,
  signal: AbortSignal | null = null,
  permission: string | null,
  employee: any | null = null,
) => {
  const config: any = {};
  if (signal) config.signal = signal;
  if (permission) config.permission = permission;

  const url = `${API_URL}/v2/cu/years/${employee ? `?employee=${employee}` : ''}`;

  const response = await axios.get(url, {
    headers: {
      'Authorization': `${token}`,
    },
    ...config,
  });

  return response.data;
};


export const getCuByYear = async (
  token: string,
  year: number,
  signal: AbortSignal | null = null,
  permission: string | null,
  employee: any | null = null,
  params: { page?: number; page_size?: number; search?: string } = {}
) => {
  const config: any = { params: {} };
  if (signal) config.signal = signal;
  if (permission) config.permission = permission;

  if (employee) config.params.employee = employee;
  if (params?.page) config.params.page = params.page;
  if (params?.page_size) config.params.page_size = params.page_size;
  if (params?.search) config.params.search = params.search;

  const response = await axios.get(`${API_URL}/v2/cu/years/${year}/`, {
    headers: { Authorization: `${token}` },
    ...config,
  });

  return response.data;
};

export const fetchDocs = async (token: string, employee: any, permission: string | null) => {
  const response = await axios.get(`${API_URL}/documenti`, {
    headers: {
      'Authorization': `${token}`,
    },
    permission,
  } as CustomAxiosRequestConfig);
  return response.data;
}

export const v2GetEmployeeDocuments = async (
  token: string,
  employee: any | null = null,
  signal: AbortSignal | null = null,
  permission: string | null,
) => {
  const config: any = {};
  if (signal) config.signal = signal;
  if (permission) config.permission = permission;

    const response = await axios.get(
    `${API_URL}/v2/dipendenti/${employee}/documents/`,
    {
      headers: {
        Authorization: `${token}`,
      },
      ...config,
    }
  );

  return response.data;
};


export const fetchDocsGroupedV2 = async (token: string, permission: string | null, params?: any) => {
  const response = await axios.get(`${API_URL}/v2/documenti`, {
    headers: { 'Authorization': `${token}` },
    params,
    permission,
  } as CustomAxiosRequestConfig);
  return response.data;
};

export const fetchDocsByEmployeeV2 = async (token: string, employee_id: any, permission: string | null) => {
  const response = await axios.get(`${API_URL}/v2/documenti/list-detail/${employee_id}`, {
    headers: {
      'Authorization': `${token}`,
    },
    permission,
  } as CustomAxiosRequestConfig);
  return response.data;
}


export const fetchCuDetail = async (token: string, id: any) => {
  const response = await axios.get(`${API_URL}/cu/${id}`, {
    headers: {
      'Authorization': `${token}`,
    }
  })
  return response.data;
}

export const deleteCu = async (token: string, id: any) => {
  const response = await axios.delete(`${API_URL}/cu/${id}`, {
    headers: {
      'Authorization': `${token}`,
    }
  })
  return response.data;
}

export const fetchBusta = async (token: string, id: any) => {
  const response = await axios.get(`${API_URL}/buste_paga/${id}`, {
    headers: {
      'Authorization': `${token}`,
    }
  })
  return response.data;
}

export const deleteBusta = async (token: string, id: any) => {
  const response = await axios.delete(`${API_URL}/buste_paga/${id}`, {
    headers: {
      'Authorization': `${token}`,
    }
  })
  return response.data;
}

export const fetchDoc = async (token: string, id: any, permission: string | null) => {
  const config: any = {};

  if (permission) 
    config.permission = permission;

  const response = await axios.get(`${API_URL}/documenti/${id}`, {
    headers: {
      'Authorization': `${token}`,
    }
  })
  return response.data;
}

export const deleteDoc = async (token: string, id: any) => {
  const response = await axios.delete(`${API_URL}/documenti/${id}`, {
    headers: {
      'Authorization': `${token}`,
    }
  })
  return response.data;
}

export const updateDocVisibility = async (token: string, id: any, visibleToEmployee: boolean) => {
  const response = await axios.patch(`${API_URL}/documenti/${id}`, {
    visible_to_employee: visibleToEmployee,
  }, {
    headers: {
      'Authorization': `${token}`,
    }
  });
  return response.data;
}


export const updateDocumentV2 = async (
  token: string,
  id: number,
  payload: {
    name?: string;
    description?: string | null;
    due_date?: string | null;               // 'YYYY-MM-DD'
    notification_email?: string | null;     // comma-separated
    notification_timing?: number | null;
  }
) => {
  const res = await axios.patch(`${API_URL}/v2/documenti/${id}`, payload, {
    headers: { 
      'Authorization': `${token}` 
    },
  });
  return res.data;
};


export const updateSignedDocumentDeadline = async (
  token: string,
  documentId: number,
  data: {
    due_date?: string | null;
    notification_timing?: number | null;
    notification_email?: string | null;
  },
  permission: string | null,
  signal: AbortSignal | null = null,
) => {
  const response = await axios.patch(
    `${API_URL}/documenti-firma/${documentId}`,
    data,
    {
      headers: {
        'Authorization': `${token}`,
      },
      permission,
      signal,
    } as CustomAxiosRequestConfig
  );

  return response.data;
};

export const fetchDocumentDeadlines = async (
  token: string,
  params: Record<string, any> = {},
  permission: string | null,
  signal: AbortSignal | null = null,
) => {
  const response = await axios.get(`${API_URL}/documenti/deadlines/`, {
    headers: {
      'Authorization': `${token}`,
    },
    params,
    permission,
    signal,
  } as CustomAxiosRequestConfig);
  return response.data;
};


export const fetchContractDeadlines = async (
  token: string,
  params: Record<string, any> = {},
  permission: string | null,
  signal: AbortSignal | null = null,
) => {
  const response = await axios.get(`${API_URL}/contratti/deadlines/`, {
    headers: {
      'Authorization': `${token}`,
    },
    params,
    permission,
    signal,
  } as CustomAxiosRequestConfig);
  return response.data;
};


export const fetchSignedDocumentDeadlines = async (
  token: string,
  params: Record<string, any> = {},
  permission: string | null,
  signal: AbortSignal | null = null,
) => {
  const response = await axios.get(`${API_URL}/documenti-firma/deadlines/`, {
    headers: {'Authorization': `${token}`},
    params,
    permission,
    signal,
  } as CustomAxiosRequestConfig);
  return response.data;
};

export const getEmployeeSignedDocuments = async (token: string, employeeId: number, signal?: AbortSignal) => {
  const response = await axios.get(`${API_URL}/firme/api/documents/`,
    {
      headers: {'Authorization': `${token}`},
      params: { employee: employeeId},
      signal,
    }
  );

  return response.data;
};


export const fetchEmployee = async (token: string, employee: any, permission: string | null, signal = null) => {
  const config: any = {};

  if (permission) 
    config.permission = permission;
  if (signal) config.signal = signal;

  const response = await axios.get(`${API_URL}/dipendenti/${employee}`, {
    headers: {
      'Authorization': `${token}`,
    },
    ...config
  })
  return response.data;
}

export const v2FetchEmployee = async (token: string, employee: any, permission: string | null, signal = null) => {
  const config: any = {};

  if (permission) 
    config.permission = permission;
  if (signal) config.signal = signal;

  const response = await axios.get(`${API_URL}/v2/dipendenti/${employee}`, {
    headers: {
      'Authorization': `${token}`,
    },
    ...config
  });
  return response.data;
}


export const uploadBustePagaMassive = async (token: string, data: any ) => {
  const response = await axios.post(`${API_URL}/upload_massivo/bp`, data, {
    headers: {
      'Authorization': `${token}`,
      'Content-Type': 'multipart/form-data',
    }
  })
  return response.data;
}

export const uploadBustePagaSingolo = async (token: string, data: any) => {
  const response = await axios.post(`${API_URL}/buste_paga/upload`, data, {
    headers: {
      'Authorization': `${token}`,
      'Content-Type': 'multipart/form-data',
    }
  })
  return response.data;
}

export const uploadCuSingolo = async (token: string, data: any) => {
  const response = await axios.post(`${API_URL}/cu/upload`, data, {
    headers: {
      'Authorization': `${token}`,
      'Content-Type': 'multipart/form-data',
    }
  })
  return response.data;
}

export const uploadDocumentiSingolo = async (token: string, data: any) => {
  const response = await axios.post(`${API_URL}/documenti/upload`, data, {
    headers: {
      'Authorization': `${token}`,
      'Content-Type': 'multipart/form-data',
    }
  })
  return response.data;
}


export const uploadCuMassive = async (token: string, data: any ) => {
  const response = await axios.post(`${API_URL}/upload_massivo/cu`, data, {
    headers: {
      'Authorization': `${token}`,
      'Content-Type': 'multipart/form-data',
    }
  })
  return response.data;
}

export const downloadReportCedolini = async (token: string, anno: any, mese: any) => {
  const response = await axios.get(`${API_URL}/report_buste_paga/${anno}/${mese}`, {
    headers: {
      'Authorization': `${token}`,
    },
    responseType: 'blob', // Ensure a Blob will be returned
  });
  
  if (response.status !== 200) {
    throw new Error(`Error status code: ${response.status}`);
  }

  const fileBlob: Blob = new Blob([response.data], { type: 'application/pdf' });

  return fileBlob;
  
};

export const downloadReportCedoliniV2 = async (token: string, anno: any, mese: any) => {
  const response = await axios.get(`${API_URL}/report_buste_paga/${anno}/${mese}/v2`, {
    headers: {
      'Authorization': `${token}`,
    },
    responseType: 'blob', // Ensure a Blob will be returned
  });
  
  if (response.status !== 200) {
    throw new Error(`Error status code: ${response.status}`);
  }

  const fileBlob: Blob = new Blob([response.data], { type: 'application/pdf' });

  return fileBlob;
  
};

export const downloadReportCu = async (token: string, anno: any) => {
  const response = await axios.get(`${API_URL}/report_certificazioni_uniche/${anno}`, {
    headers: {
      'Authorization': `${token}`,
    },
    responseType: 'blob', // Ensure a Blob will be returned
  });
  
  if (response.status !== 200) {
    throw new Error(`Error status code: ${response.status}`);
  }

  const fileBlob: Blob = new Blob([response.data], { type: 'application/pdf' });

  return fileBlob;
  
};

export const downloadReportCuV2 = async (token: string, anno: any) => {
  const response = await axios.get(`${API_URL}/report_certificazioni_uniche/${anno}/v2`, {
    headers: {
      'Authorization': `${token}`,
    },
    responseType: 'blob', // Ensure a Blob will be returned
  });
  
  if (response.status !== 200) {
    throw new Error(`Error status code: ${response.status}`);
  }

  const fileBlob: Blob = new Blob([response.data], { type: 'application/pdf' });

  return fileBlob;
  
};


export const createDipendente = async (token: string, data: any) => {
  // values = {first_name: 'dasd', last_name: 'asd', tax_code: 'asdas', email: 'asdasd'}
  const response = await axios.post(`${API_URL}/dipendenti`, data, {
    headers: {
      'Authorization': `${token}`,
      'Content-Type': 'multipart/form-data',
    }
  })
  return response.data;
}

export const createDipendenteByUnilav = async (token: string, data: any) => {
  // values = {first_name: 'dasd', last_name: 'asd', tax_code: 'asdas', email: 'asdasd'}
  const response = await axios.post(`${API_URL}/dipendenti/unilav/create`, data, {
    headers: {
      'Authorization': `${token}`,
      'Content-Type': 'multipart/form-data',
    }
  })
  return response.data;
}

export const bulkCreateEmployees = async (token: string, data: any) => {
  const response = await axios.post(`${API_URL}/dipendenti/bulk-create/`, data, {
    headers: {
      'Authorization': `${token}`,
    },
  });
  return response.data;
};


export const updateDipendente = async (token: string, data: any) => {
  // values = {first_name: 'dasd', last_name: 'asd', tax_code: 'asdas', email: 'asdasd'}
  const response = await axios.post(`${API_URL}/dipendenti/${data.cf}`, data, {
    headers: {
      'Authorization': `${token}`,
      'Content-Type': 'multipart/form-data',
    }
  })
  return response.data;
}


export const deleteDipendente = async (token: string, cf: string) => {
  const response = await axios.delete(`${API_URL}/dipendenti/${cf}/delete`, {
    headers: {
      'Authorization': `${token}`,
    }
  })
  return response.data;
}


export const reassignDoc = async (token: string, doc_id: number, doc_type: string,  employee_cf: string) => {
  // values = {first_name: 'dasd', last_name: 'asd', tax_code: 'asdas', email: 'asdasd'}
  const response = await axios.post(`${API_URL}/docs/${doc_type}/${doc_id}/reassign`, {employee_cf}, {
    headers: {
      'Authorization': `${token}`,
      'Content-Type': 'multipart/form-data',
    }
  })
  return response.data;
}


export const confirmEmpDoc = async (token: string, doc_id: number, doc_type: string) => {
  const response = await axios.post(`${API_URL}/docs/${doc_type}/${doc_id}/confirm`, {}, {
    headers: {
      'Authorization': `${token}`,
      'Content-Type': 'multipart/form-data',
    }
  })
  return response.data;
}

export const deleteEmpDoc = async (token: string, doc_id: number, doc_type: string) => {
  const response = await axios.post(`${API_URL}/docs/${doc_type}/${doc_id}/delete`, {}, {
    headers: {
      'Authorization': `${token}`,
      'Content-Type': 'multipart/form-data',
    }
  })
  return response.data;
}

export const confirmAllEmpDocs = async (token: string, payload: any, doc_type: string) => {
  // payload: { year: number, month: number }
  const response = await axios.put(`${API_URL}/hr/docs/${doc_type}/ops-all/`, 
  payload,
  {
    headers: {
      'Authorization': `${token}`,
      'Content-Type': 'multipart/form-data',
    }
  })
  return response.data;
}

export const deleteAllEmpDocs = async (token: string, payload: any, doc_type: string) => {
  const response = await axios.delete(`${API_URL}/hr/docs/${doc_type}/ops-all/`, 
  {
    headers: {
      'Authorization': `${token}`,
      'Content-Type': 'multipart/form-data',
    },
    data: payload,
  })
  return response.data;
}


// export const updateDipendente = async (token: string, data: any, employee: any) => {
//   // values = {first_name: 'dasd', last_name: 'asd', tax_code: 'asdas', email: 'asdasd'}
//   const response = await axios.put(`${API_URL}/dipendenti/${employee}`, data, {
//     headers: {
//       'Authorization': `${token}`
//     }
//   })
//   return response.data;
// }

export const resetDipendentePassword = async (token: string, id: any) => {
  // values = {first_name: 'dasd', last_name: 'asd', tax_code: 'asdas', email: 'asdasd'}
  const response = await axios.post(`${API_URL}/dipendenti/${id}/reset_password`, {}, {
    headers: {
      'Authorization': `${token}`
    }
  })
  return response.data;
}

export const changeAppEnable = async (token: string, id: any, status: string) => {
  const response = await axios.post(`${API_URL}/dipendenti/${id}/app/${status}`, {}, {
    headers: {
      'Authorization': `${token}`
    }
  })
  return response.data;
}

export const getLivelli = async (token: string) => {
  const response = await axios.get(`${API_URL}/livelli`, {
    headers: {
      'Authorization': `${token}`,
    }
  });

  return response.data;
}

export const selezionaLivello = async (token: string, plan_id: string, qnt: number, coupon?: string, package_id?: string | null) => {
  const params = new URLSearchParams({
    pk: plan_id,
    qnt: qnt.toString(),
  });

  if (typeof coupon === 'string' && coupon.trim() !== '') {
    params.append('coupon', coupon);
  }

  if (package_id) {
    params.append('package_pk', package_id);
  }

  try {
    const response = await axios.get(`${API_URL}/livelli/selezione?${params.toString()}`, {
      headers: {
        'Authorization': `${token}`,
      }
    });

    return response.data;
  } catch (error) {
    console.error('Errore nella selezione del livello:', error);
    throw error;
  }
};

export const cancellaLivello = async (token: string, level_id: number) => {
  const response = await axios.delete(`${API_URL}/livelli/cancella/${level_id}`, {
    headers: {
      'Authorization': `${token}`,
    }
  });

  return response.data;
}

export const getPaymentInfo = async (token: string) => {

  const response = await axios.get(`${API_URL}/profile/payment-info`, {
    headers: {
      'Authorization': `${token}`,
    }
  });
  // Return the search results
  return response.data;
};

export const setPaymentInfo = async (token: string, data: string) => {

  const response = await axios.post(`${API_URL}/profile/payment-info`, data,
  {
    headers: {
      'Authorization': `${token}`,
    }
  });
  // Return the search results
  return response.data;
};


export const recover = async (data: Record<string, any>) => {
  const body: Record<string, any> = data

  if (import.meta.env.VITE_APP_ENV === 'development') {
    body['debug'] = true
  }

  const response = await axios.post(`${API_URL}/recover`, body, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  return response.data;
}

export const changePassword = async (token: string, data: any) => {
  // profile/change_password
  const response = await axios.post(`${API_URL}/profile/change_password`, data, {
    headers: {
      'Authorization': `${token}`,
      'Content-Type': 'multipart/form-data',
    }
  });

  return response.data;
}

export const updateProfile = async (token: string, data: any) => {
  // values = {first_name: 'dasd', last_name: 'asd', tax_code: 'asdas', email: 'asdasd'}
  const response = await axios.post(`${API_URL}/profile`, data, {
    headers: {
      'Authorization': `${token}`,
      'Content-Type': 'multipart/form-data',
    }
  })
  return response.data;
}

export const getProfile = async (token: string) => {
  const response = await axios.get(`${API_URL}/profile`, {
    headers: {
      'Authorization': `${token}`,
    }
  });

  return response.data;
}

/**
 * Function to fetch consulenti data based on the input value.
 * @param {string} inputValue - The input value to search for.
 * @param {string} token - authorizaion token.
 * @returns {Array} - Array of search results or null in case of error.
 */
export const fetchConsulenti = async (token:string, inputValue: string) => {
  // Create a new CancelToken
  const newSource = axios.CancelToken.source();

  // Making the API call
  const response = await axios.get(`${API_URL}/consulenti/search`, {
    params: { q: inputValue },
    headers: {
      'Authorization': `${token}`,
    },
    cancelToken: newSource.token
  });
  // Return the search results
  return response.data;
};

export const assignConsulente = async (token: string, consulente_id: string) => {
  // Create a new CancelToken
  // Making the API call
  const response = await axios.post(`${API_URL}/consulenti`, {
    c_id: consulente_id
  }, {
    headers: {
      'Authorization': `${token}`,
      'Content-Type': 'multipart/form-data',
    }
  });
  // Return the search results
  return response.data;
};

export const getConsulenti = async (token: string) => {
  // Create a new CancelToken
  // Making the API call
  const response = await axios.get(`${API_URL}/consulenti`, {
    headers: {
      'Authorization': `${token}`,
    }
  });
  // Return the search results
  return response.data;
};

export const deleteConsulente = async (token: string, consulente_id: string) => {
  // Create a new CancelToken
  // Making the API call
  const response = await axios.post(`${API_URL}/consulenti/${consulente_id}/delete`, {
    c_id: consulente_id
  }, {
    headers: {
      'Authorization': `${token}`,
      'Content-Type': 'multipart/form-data',
    }
  });
  // Return the search results
  return response.data;
};


export const getBillingData = async (token: string) => {

  const response = await axios.get(`${API_URL}/profile/billing`, {
    headers: {
      'Authorization': `${token}`,
    }
  });
  // Return the search results
  return response.data;
};

export const setBillingData = async (token: string, data: any) => {

  const response = await axios.put(`${API_URL}/profile/billing`, data,
  {
    headers: {
      'Authorization': `${token}`,
    }
  });
  // Return the search results
  return response.data;
};



export const sendCredentialEmployee = async (token: string, id: string) => {
  const response = await axios.get(`${API_URL}/dipendenti/${id}/send-credential/`, {
    headers: {
      'Authorization': `${token}`,
    },
  });
  return response.data;
};


export const getDocumentRecipientsDefault  = async (token: string, employeeId: number) => {
  const response = await axios.get(`${API_URL}/documenti/document-recipients-default`, {
    params: { employee_id: employeeId },
    headers: {
      'Authorization': `${token}`,
    },
  });
  return response.data;
}

export const getContractRecipientsDefault  = async (token: string, employeeId: number) => {
  const response = await axios.get(`${API_URL}/contratti/contract-recipients-default`, {
    params: { employee_id: employeeId },
    headers: {
      'Authorization': `${token}`,
    },
  });
  return response.data;
}

